import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileDown, FileText,ExternalLink  } from "lucide-react";
import LogoFallback from "/img/logo.png";
import he from 'he';

export default function CareerDetails({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Career | University of Creative Technology Chittagong",
}) {
	const { id } = useParams(); // Assuming dynamic route like /career/:id
	const [career, setCareer] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const fetchCareerDetails = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				id:id,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/career-details", payload);
			
			
			setCareer(response.data[0]);
			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
  
	useEffect(() => {
		fetchCareerDetails();
	}, [id]);
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	if (loading) {
		return <p className="text-center py-20 text-[#015A7A]">Loading...</p>;
	}

	if (error) {
		return <p className="text-center py-20 text-red-500">{error}</p>;
	}

	if (!career) {
		return <p className="text-center py-20 text-[#015A7A]">No data found.</p>;
	}
	const categories = ["Any", "Academic", "Non Academic"];
	function textformat(textdata){
		let textinfo="";
		textinfo=textdata.replace(/\\n/g, '<br />');
		textinfo=textinfo.split('\\n').join('\n');
		textinfo=textinfo.split('\n').join('<br />');
		textinfo=textinfo.split('\\r').join(' ');
		textinfo=textinfo.split('\\').join(' ');
		return textinfo;
	}
  return (
    <div className="relative bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] overflow-hidden">
      {/* Hero Section */}
	  <section className="relative py-20 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[#fff8e1] opacity-40 -skew-y-3"></div>
        <motion.h1
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-4xl md:text-5xl font-bold text-[#015A7A]"
        >
          {career.designation_name}  <span className="text-[#014864]">in {career.department_name}</span>
        </motion.h1>
        <p className="relative z-10 mt-4 text-gray-700 max-w-2xl mx-auto">
          Career Opportunity Details
        </p>
		{/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 -mt-6">
          <svg
            viewBox="0 0 1440 80"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-20"
          >
            <path
              d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z"
              fill="#014864"
              opacity="0.92"
            />
          </svg>
        </div>
      </section>
     

      {/* Career Details */}
      <section className="max-w-5xl mx-auto py-16 px-6">
        <div className="bg-white shadow-xl rounded-2xl p-8 space-y-6">
          <motion.div 
			initial={{ opacity: 0, x: 40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.7 }}	
		  className="grid md:grid-cols-2 gap-6">
            <Detail label="Number of Position" value={career.position_number} />
            <Detail label="Gender" value={career.gender && career.gender.trim() !== "" ? career.gender : "For All"} />
            <Detail label="Department" value={career.department_name} />
            <Detail label="Designation" value={career.designation_name} />
            <Detail
              label="Opening Date"
              value={new Date(career.open_date).toLocaleDateString()}
            />
            <Detail
              label="Deadline"
              value={new Date(career.end_date).toLocaleDateString()}
            />
            <Detail label="Job Type" value={career.job_nature} />
            <Detail label="Specialization" value={career.subject && career.subject.trim() !== "" ? career.subject : "Relevant"} />
          </motion.div>

          <motion.div 
			initial={{ opacity: 0, x: 40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.7 }}	
		  >
            <h3 className="font-bold text-lg text-teal-700 mb-2">Qualification</h3>
			<div
			  className="overflow-hidden break-words prose max-w-full"
			  dangerouslySetInnerHTML={{ __html: textformat(career.education_requirements) }}
			></div>
           
          </motion.div>

          <motion.div
			initial={{ opacity: 0, x: 40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.7 }}	
		  >
            <h3 className="font-bold text-lg text-teal-700 mb-2">Responsibilities</h3>
			<div
			  className="overflow-hidden break-words prose max-w-full"
			  dangerouslySetInnerHTML={{ __html: textformat(career.job_context) }}
			></div>
           
          </motion.div>

          <motion.div
			initial={{ opacity: 0, x: 40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.7 }}	
		  >
            <h3 className="font-bold text-lg text-teal-700 mb-2">
              Job Description
            </h3>
			<div
			  className="overflow-hidden break-words prose max-w-full"
			  dangerouslySetInnerHTML={{ __html: textformat(career.job_description) }}
			></div>
           
          </motion.div>
			
			<motion.div
				initial={{ opacity: 0, x: 40 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}	
			>
				<h3 className="font-bold text-lg text-teal-700 mb-2">
				  Job Requirements
				</h3>
				<div
				  className="overflow-hidden break-words prose max-w-full"
				  dangerouslySetInnerHTML={{ __html: textformat(career.job_requirements) }}
				></div>
				
			</motion.div>
			<motion.div
				initial={{ opacity: 0, x: 40 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}	
			>
				<h3 className="font-bold text-lg text-teal-700 mb-2">
				  Job Location
				</h3>
				
				<p className="text-gray-700 whitespace-pre-line">
				  {career.job_location}
				</p>
			</motion.div>
			
			<motion.div
				initial={{ opacity: 0, x: 40 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}	
			>
				<h3 className="font-bold text-lg text-teal-700 mb-2">
				  Apply Instruction
				</h3>
				<p className="text-gray-700 whitespace-pre-line">
				  {career.application_instruction}
				</p>
			</motion.div>
			
          {/* Buttons */}
          <motion.div 
			initial={{ opacity: 0, x: 40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true }}
			transition={{ duration: 0.7 }}	
		  className="flex flex-wrap gap-4 pt-6">
            
            <a
              href={`${import.meta.env.VITE_API_SOURCE_URL}${career.advertisement}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#015A7A] text-white px-5 py-3 rounded-xl hover:bg-[#014864] transition"
            >
              <FileDown className="w-5 h-5" />
              Download Job Advertisement
            </a>
			
			<a
              href="https://career.uctc.ac.bd"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-orange-600 text-white px-5 py-3 rounded-xl hover:bg-[#014864] transition"
            >
              <ExternalLink className="w-5 h-5" />
              Apply Now
            </a>
			<a
              href="/job-admit-card"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-yellow-400 text-white px-5 py-3 rounded-xl hover:bg-[#014864] transition"
            >
              <FileDown className="w-5 h-5" />
              Admit Card
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-sm text-[#015A7A]">{label}</p>
      <p className="text-[#014864] font-medium">{value || "—"}</p>
    </div>
  );
}
